import {makeAutoObservable} from 'mobx';

export type ThemeMode = 'light' | 'dark' | 'system';

const STORAGE_KEY = 'visor-theme';

function getSystemPrefersDark(): boolean {
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function resolveTheme(mode: ThemeMode): 'light' | 'dark' {
    if (mode === 'dark') {
        return 'dark';
    }
    if (mode === 'light') {
        return 'light';
    }
    return getSystemPrefersDark() ? 'dark' : 'light';
}

function applyResolvedTheme(resolved: 'light' | 'dark') {
    const root = document.documentElement;
    root.classList.toggle('dark', resolved === 'dark');
    root.style.colorScheme = resolved;
}

class ThemeStore {
    public mode: ThemeMode = 'system';
    public resolved: 'light' | 'dark' = 'light';
    private mediaQuery?: MediaQueryList;
    private mediaListener?: (event: MediaQueryListEvent) => void;

    constructor() {
        makeAutoObservable(this);
    }

    public get isDark(): boolean {
        return this.resolved === 'dark';
    }

    public init() {
        const stored = localStorage.getItem(STORAGE_KEY) as ThemeMode | null;
        if (stored === 'light' || stored === 'dark' || stored === 'system') {
            this.mode = stored;
        }
        this.apply();

        this.mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        this.mediaListener = () => {
            if (this.mode === 'system') {
                this.apply();
            }
        };
        this.mediaQuery.addEventListener('change', this.mediaListener);
    }

    public dispose() {
        if (this.mediaQuery && this.mediaListener) {
            this.mediaQuery.removeEventListener('change', this.mediaListener);
        }
    }

    public setMode(mode: ThemeMode) {
        this.mode = mode;
        localStorage.setItem(STORAGE_KEY, mode);
        this.apply();
    }

    public toggle() {
        this.setMode(this.resolved === 'dark' ? 'light' : 'dark');
    }

    private apply() {
        const resolved = resolveTheme(this.mode);
        this.resolved = resolved;
        applyResolvedTheme(resolved);
    }
}

const themeStore = new ThemeStore();

if (typeof window !== 'undefined') {
    const stored = localStorage.getItem(STORAGE_KEY) as ThemeMode | null;
    if (stored === 'light' || stored === 'dark' || stored === 'system') {
        themeStore.mode = stored;
    }
    themeStore.resolved = resolveTheme(themeStore.mode);
}

export default themeStore;
